import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatEUR, formatDate } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { WarningCircle } from "@phosphor-icons/react";
import { ReqStatus, REQ_STATUS, rentLine } from "./RequestWorkflow";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const ONLY = [["open", "Laufend"], ["overdue", "Handlungsbedarf"], ["closed", "Abgeschlossen"], ["", "Alle"]];

const TONE = { slate: "text-slate-800", rose: "text-rose-700", indigo: "text-indigo-800", emerald: "text-emerald-700" };

function Kpi({ label, value, testId, tone = "slate" }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2" data-testid={testId}>
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${TONE[tone]}`}>{value}</div>
    </div>
  );
}

export default function RequestsOverview() {
  const [d, setD] = useState(null);
  const [only, setOnly] = useState("open");
  const [status, setStatus] = useState("");
  const [prop, setProp] = useState("");
  const [owner, setOwner] = useState("");
  const [q, setQ] = useState("");
  const [tick, setTick] = useState(0);
  const { user } = useAuth();
  useEffect(() => {
    api.get("/rent-increase/requests", { params: { only: only || undefined, status: status || undefined } }).then(({ data }) => setD(data));
  }, [only, status, tick]);
  const runReminders = async () => {
    try {
      const { data: r } = await api.post("/rent-increase/reminders/run");
      toast.success(`Erinnerungen geprüft: ${r.drafts + r.approvals + r.reminders + r.expired + r.klage} neue Aufgabe(n), ${r.auto_letters || 0} Erinnerungsschreiben erstellt, ${r.applied} Miete(n) übernommen`);
      setTick((x) => x + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };
  const items = d?.items || [];
  const props = useMemo(() => [...new Map(items.filter((r) => r.property_id).map((r) => [r.property_id, r.property_name])).entries()], [items]);
  const owners = useMemo(() => [...new Map(items.filter((r) => r.owner_contact_id).map((r) => [r.owner_contact_id, r.owner_name])).entries()], [items]);
  const rows = items.filter((r) => (!prop || r.property_id === prop) && (!owner || r.owner_contact_id === owner)
    && (!q || [r.contract_number, r.property_name, r.case_number, r.owner_name, r.tenant_names].filter(Boolean).join(" ").toLowerCase().includes(q.toLowerCase())));
  const st = d?.stats || {};
  const sel = "h-8 rounded border border-slate-300 text-xs px-2";
  return (
    <div className="space-y-4" data-testid="ri-overview">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Kpi label="Laufend" value={st.open ?? "—"} testId="ri-ov-open" />
        <Kpi label="Handlungsbedarf" value={st.overdue ?? "—"} tone="rose" testId="ri-ov-overdue" />
        <Kpi label="Mehr / Monat in Aussicht" value={formatEUR(st.monthly_pending || 0)} tone="indigo" testId="ri-ov-pending" />
        <Kpi label="Mehr / Monat umgesetzt" value={formatEUR(st.monthly_applied || 0)} tone="emerald" testId="ri-ov-applied" />
        <Kpi label="Mehreinnahmen / Jahr" value={formatEUR(st.yearly_applied || 0)} tone="emerald" testId="ri-ov-yearly" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {ONLY.map(([k, l]) => (
          <button key={k || "all"} type="button" onClick={() => setOnly(k)} data-testid={`ri-ov-only-${k || "all"}`}
            className={`px-2.5 py-1 text-xs rounded border ${only === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-700"}`}>{l}</button>
        ))}
        <select className={sel} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="ri-ov-status">
          <option value="">Alle Status</option>
          {Object.entries(REQ_STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select className={sel} value={prop} onChange={(e) => setProp(e.target.value)} data-testid="ri-ov-property">
          <option value="">Alle Objekte</option>{props.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
        </select>
        <select className={sel} value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="ri-ov-owner">
          <option value="">Alle Eigentümer</option>{owners.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
        </select>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Mieter, Vertrag, Objekt, Aktenzeichen…" className="w-56 h-8 ml-auto" data-testid="ri-ov-search" />
        {["admin", "super_admin"].includes(user?.role) && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={runReminders} data-testid="ri-ov-run-reminders">Erinnerungen jetzt prüfen</Button>}
      </div>
      <div className="border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="ri-ov-table">
          <thead className="bg-slate-50 text-xs text-slate-500 text-left">
            <tr><th className="px-3 py-2">Mietvertrag</th><th>Objekt / Eigentümer</th><th>Status</th><th className="text-right">Miete</th><th className="px-3">Zustimmung bis</th><th>Wirksam ab</th><th>Klagefrist</th><th className="px-3">Aktenzeichen</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={`border-t border-slate-100 ${r.overdue ? "bg-rose-50/50" : "hover:bg-slate-50"}`} data-testid={`ri-ov-row-${r.id}`}>
                <td className="px-3 py-2"><Link to={`/mietverhaeltnisse/${r.tenancy_id}?tab=mieterhoehung`} className="text-teal-700 hover:underline font-mono text-xs">{r.contract_number}</Link>
                  {r.tenant_names && <div className="text-xs text-slate-700 truncate max-w-[220px]" title={r.tenant_names} data-testid={`ri-ov-tenants-${r.id}`}>{r.tenant_names}</div>}</td>
                <td className="text-xs">{r.property_name}<div className="text-[11px] text-slate-500">{r.owner_name || ""}</div></td>
                <td className="text-xs"><span className="inline-flex items-center gap-1">{r.overdue && <WarningCircle size={14} className="text-rose-600" />}<ReqStatus status={r.status} testId={`ri-ov-status-${r.id}`} /></span></td>
                <td className="text-right tabular-nums text-xs">{rentLine(r)}</td>
                <td className="px-3 text-xs">{formatDate(r.consent_deadline)}</td>
                <td className="text-xs">{formatDate(r.effective_date)}</td>
                <td className="text-xs">{r.klage_deadline ? formatDate(r.klage_deadline) : "—"}</td>
                <td className="px-3 text-[11px] font-mono text-slate-500">{r.case_number || "—"}</td>
              </tr>
            ))}
            {d && rows.length === 0 && <tr><td colSpan={8} className="text-center text-sm text-slate-500 py-8" data-testid="ri-ov-empty">Keine Mieterhöhungen für diesen Filter.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
